import { useState, useEffect } from "react";
import RichText, { PLACEHOLDERS } from "./RichText";
import ImagePicker from "./ImagePicker";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus, ArrowUp, ArrowDown, Trash2, Type, Heading, Image as ImageIcon,
  SeparatorHorizontal, ScissorsLineDashed, Puzzle, BookOpen, GripVertical,
} from "lucide-react";

const WIDGETS = {
  cover: "Cover banner",
  meta: "Proposal details table",
  testimonials: "Client testimonials",
  pricing: "Pricing — Your investment",
  catchup: "Catch-up fees",
  assumptions: "Scope & assumptions",
  signature: "Signature certificate",
  what_next: "What happens next",
  contact: "Thank you & contact",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Math.random()).slice(2));
const imgUrl = (p) => (p ? `${BACKEND}/api/public/files/${p}` : "");

const TYPE_LABEL = {
  heading: "Heading", text: "Rich text", image: "Image",
  page_break: "Page break", divider: "Divider", dynamic: "Section",
};

function SortableBlock({ id, testid, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="border border-border rounded-md bg-white" data-testid={testid}>
      {children(listeners, attributes)}
    </div>
  );
}

export default function DocumentEditor({ value, onChange }) {
  const [lib, setLib] = useState([]);
  useEffect(() => { api.get("/content-library").then((r) => setLib(r.data)).catch(() => {}); }, []);
  const blocks = value?.blocks || [];
  const setBlocks = (b) => onChange({ ...(value || {}), blocks: b });
  const update = (i, patch) => setBlocks(blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const remove = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const b = [...blocks];
    [b[i], b[j]] = [b[j], b[i]];
    setBlocks(b);
  };
  const add = (block) => setBlocks([...blocks, { id: uid(), ...block }]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const blockId = (b, i) => b.id || `b${i}`;
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = blocks.map(blockId);
    const from = ids.indexOf(active.id), to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    setBlocks(arrayMove(blocks, from, to));
  };
  const [pickerFor, setPickerFor] = useState(null);
  const [libOpen, setLibOpen] = useState(false);

  const upload = async (i, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd);
      update(i, { image_path: data.path });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const appendField = (i, field, token) => {
    if (!token) return;
    const cur = blocks[i][field] || "";
    update(i, { [field]: `${cur}{{${token}}}` });
  };

  return (
    <div className="space-y-3" data-testid="document-editor">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={blocks.map(blockId)} strategy={verticalListSortingStrategy}>
          {blocks.map((b, i) => (
            <SortableBlock key={b.id || i} id={blockId(b, i)} testid={`block-${i}`}>
              {(dragListeners, dragAttrs) => (
                <>
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-cass-bone/50">
                    <span className="text-xs font-semibold text-cass-navy uppercase tracking-wide flex items-center gap-1.5">
                      <button type="button" {...dragAttrs} {...dragListeners} className="cursor-grab text-cass-slate hover:text-cass-navy" data-testid={`block-drag-${i}`}><GripVertical className="w-4 h-4" /></button>
                      {TYPE_LABEL[b.type] || b.type}{b.type === "dynamic" && <span className="text-cass-slate font-normal normal-case">· {WIDGETS[b.widget] || b.widget}</span>}
                    </span>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-cass-slate hover:text-cass-navy disabled:opacity-30" data-testid={`block-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === blocks.length - 1} className="p-1 text-cass-slate hover:text-cass-navy disabled:opacity-30" data-testid={`block-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                      <button type="button" onClick={() => remove(i)} className="p-1 text-cass-slate hover:text-red-600" data-testid={`block-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="p-3 space-y-2">
                    {b.type === "heading" && (
                      <div className="flex items-start gap-2">
                        <Select value={String(b.level || 2)} onValueChange={(v) => update(i, { level: Number(v) })}>
                          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">H1</SelectItem>
                            <SelectItem value="2">H2</SelectItem>
                            <SelectItem value="3">H3</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input value={b.text || ""} onChange={(e) => update(i, { text: e.target.value })} placeholder="Heading text" data-testid={`heading-input-${i}`} />
                        <select onChange={(e) => { appendField(i, "text", e.target.value); e.target.value = ""; }} defaultValue="" className="text-xs border border-border rounded px-2 py-2 text-cass-slate bg-white shrink-0">
                          <option value="" disabled>+ Field</option>
                          {PLACEHOLDERS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                      </div>
                    )}

                    {b.type === "text" && (
                      <RichText value={b.html || ""} onChange={(html) => update(i, { html })} />
                    )}

                    {b.type === "image" && (
                      <div className="space-y-2">
                        {b.image_path
                          ? <img src={imgUrl(b.image_path)} alt="" className="max-h-56 rounded border border-border" data-testid={`image-preview-${i}`} />
                          : <div className="h-28 rounded border border-dashed border-border grid place-items-center text-cass-slate text-sm">No image yet</div>}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button type="button" variant="outline" size="sm" onClick={() => setPickerFor(i)} data-testid={`image-choose-${i}`}>
                            <ImageIcon className="w-4 h-4 mr-1" />{b.image_path ? "Replace / choose" : "Choose image"}
                          </Button>
                          <Select value={String(b.width_pct || 100)} onValueChange={(v) => update(i, { width_pct: Number(v) })}>
                            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="25">25% width</SelectItem>
                              <SelectItem value="33">33% width</SelectItem>
                              <SelectItem value="50">50% width</SelectItem>
                              <SelectItem value="75">75% width</SelectItem>
                              <SelectItem value="100">Full width</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Input value={b.caption || ""} onChange={(e) => update(i, { caption: e.target.value })} placeholder="Caption (optional)" />
                      </div>
                    )}

                    {b.type === "page_break" && <p className="text-sm text-cass-slate">↵ Forces a new page in the PDF (ignored on the web microsite).</p>}
                    {b.type === "divider" && <p className="text-sm text-cass-slate">A horizontal divider line.</p>}

                    {b.type === "dynamic" && (
                      <div className="space-y-2">
                        <p className="text-sm text-cass-slate">Auto-generated from proposal data: <b>{WIDGETS[b.widget] || b.widget}</b>.</p>
                        {b.widget === "cover" && (
                          <div className="grid grid-cols-2 gap-2">
                            <div><Label className="text-xs">Subtitle</Label><Input value={b.subtitle || ""} onChange={(e) => update(i, { subtitle: e.target.value })} /></div>
                            <div><Label className="text-xs">Tagline</Label><Input value={b.tagline || ""} onChange={(e) => update(i, { tagline: e.target.value })} /></div>
                          </div>
                        )}
                        {b.widget === "catchup" && (
                          <div><Label className="text-xs">Explanation</Label><Textarea rows={3} value={b.explanation || ""} onChange={(e) => update(i, { explanation: e.target.value })} /></div>
                        )}
                        {b.widget === "contact" && (
                          <div className="space-y-2">
                            <div><Label className="text-xs">Thank-you text</Label><Textarea rows={2} value={b.thank_you_text || ""} onChange={(e) => update(i, { thank_you_text: e.target.value })} /></div>
                            <div className="grid grid-cols-2 gap-2">
                              <div><Label className="text-xs">Phone</Label><Input value={b.phone || ""} onChange={(e) => update(i, { phone: e.target.value })} /></div>
                              <div><Label className="text-xs">Email</Label><Input value={b.email || ""} onChange={(e) => update(i, { email: e.target.value })} /></div>
                            </div>
                            <div><Label className="text-xs">Opening hours</Label><Input value={b.hours || ""} onChange={(e) => update(i, { hours: e.target.value })} /></div>
                            <div><Label className="text-xs">Address</Label><Input value={b.address || ""} onChange={(e) => update(i, { address: e.target.value })} /></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </SortableBlock>
          ))}
        </SortableContext>
      </DndContext>

      <div className="border border-dashed border-border rounded-md p-3 flex flex-wrap items-center gap-2" data-testid="add-block-bar">
        <span className="text-xs font-semibold text-cass-slate mr-1">Add:</span>
        <Button variant="outline" size="sm" onClick={() => add({ type: "heading", level: 2, text: "New heading" })} data-testid="add-heading"><Heading className="w-4 h-4 mr-1" />Heading</Button>
        <Button variant="outline" size="sm" onClick={() => add({ type: "text", html: "<p>Write here…</p>" })} data-testid="add-text"><Type className="w-4 h-4 mr-1" />Text</Button>
        <Button variant="outline" size="sm" onClick={() => add({ type: "image", width_pct: 100 })} data-testid="add-image"><ImageIcon className="w-4 h-4 mr-1" />Image</Button>
        <Button variant="outline" size="sm" onClick={() => add({ type: "page_break" })} data-testid="add-pagebreak"><ScissorsLineDashed className="w-4 h-4 mr-1" />Page break</Button>
        <Button variant="outline" size="sm" onClick={() => add({ type: "divider" })} data-testid="add-divider"><SeparatorHorizontal className="w-4 h-4 mr-1" />Divider</Button>
        <Select value="" onValueChange={(w) => add({ type: "dynamic", widget: w })}>
          <SelectTrigger className="w-52 h-9" data-testid="add-section"><span className="flex items-center gap-1 text-sm"><Puzzle className="w-4 h-4" />Add section…</span></SelectTrigger>
          <SelectContent>
            {Object.entries(WIDGETS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        {lib.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setLibOpen(true)} data-testid="insert-library"><BookOpen className="w-4 h-4 mr-1" />From library…</Button>
        )}
      </div>

      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="library-picker-dialog">
          <DialogHeader>
            <DialogTitle>Insert from content library</DialogTitle>
            <DialogDescription>Preview how each snippet will look inside the proposal, then insert it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {lib.map((x) => (
              <div key={x.id} className="border border-border rounded-md overflow-hidden" data-testid={`lib-preview-${x.id}`}>
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-cass-bone/60 border-b border-border">
                  <div className="min-w-0"><span className="text-[11px] uppercase tracking-wide text-cass-slate">{x.category}</span><div className="font-medium text-cass-navy text-sm truncate">{x.name}</div></div>
                  <Button size="sm" className="bg-cass-navy text-white shrink-0" data-testid={`insert-lib-${x.id}`}
                    onClick={() => { setBlocks([...blocks, { id: uid(), type: "text", html: x.html || "" }]); setLibOpen(false); toast.success(`Inserted “${x.name}”`); }}>
                    <Plus className="w-4 h-4 mr-1" />Insert
                  </Button>
                </div>
                <div className="doc-prose px-4 py-3 text-sm max-h-56 overflow-y-auto bg-white" dangerouslySetInnerHTML={{ __html: x.html || "<p class='text-cass-slate'>(empty)</p>" }} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <ImagePicker open={pickerFor !== null} onOpenChange={(o) => !o && setPickerFor(null)}
        onSelect={(path) => { if (pickerFor !== null) update(pickerFor, { image_path: path }); }} />
    </div>
  );
}
